import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid, Autocomplete, TextField } from '@mui/material';
import { Card } from '@mui/joy';
import { calculateAvgValues, calculateMinCo2Mode, calculateOffset } from '../../helpers/Functions.js';
import { defaultProps, getCityFromName, getCityScores, getCityTemperature } from 'helpers/data.js';
import { Colors } from 'helpers/Colors.js';
import { getCitySeasonality, getidFromCity, getCityFromid } from 'helpers/data.js';
import Chart from 'react-apexcharts';
import CityCardTable from 'components/city/CityCardTable.jsx';
// @ts-ignore
import destinations from '../../data/data5.json';
import { useNavigationContext } from 'context/NavigationContext.jsx';

const Compare = ({ filteredDestinations, from }) => {
    // const { state } = useLocation();
    // const fromDestination = state && JSON.parse(state.fromDestination);
    // const interests = state && JSON.parse(state.interests);
    // const month = state && state.month;
    const { fromDestination, interests, month } = useNavigationContext();
    const averages = calculateAvgValues(fromDestination, filteredDestinations, month);
    const [cities, setCities] = useState([]);
    const [city1, setCity1] = useState('');
    const [city2, setCity2] = useState('');
    const [city3, setCity3] = useState('');
    const [city1Name, setCity1Name] = useState('');
    const [city2Name, setCity2Name] = useState('');
    const [city3Name, setCity3Name] = useState('');
    const xLabels = ['total', 'cost', 'family', 'fun', 'english speaking', 'nightlife'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const variables = ['cost', 'duration_sec', 'co2_kg']

    useEffect(() => {
        filteredDestinations.map((dest, index) => {
            // Check if the index is less than 3 to log the first 3 values
            if (index === 0) {
                setCity1Name(dest.name);
            } else if (index === 1) {
                setCity2Name(dest.name);
            } else if (index === 2) {
                setCity3Name(dest.name);
            }
        });
    }, [filteredDestinations]);

    useEffect(() => {
        updateData();
    }, [city1Name, city2Name, city3Name]);

    const getTrainVal = (cn, isCost) => {
        let t = getCityFromName(cn)?.connections_train.filter((connection) => connection.to_id === from.id)[0]
        return t === null ? [] : (isCost ? Math.round(t?.cost) : (t?.co2_kg))
    }

    const getFlightVal = (cn, isCost) => {
        let t = getCityFromName(cn)?.connections_flight.filter((connection) => connection.to_id === from.id)[0]
        return t === null ? [] : (isCost ? Math.round(t?.cost) : (t?.co2_kg))
    }

    const getDrivingVal = (cn, isCost) => {
        let t = getCityFromName(cn)?.connections_driving.filter((connection) => connection.to_id === from.id)[0]
        return t === null ? [] : (isCost ? Math.round(t?.cost) : (t?.co2_kg))
    }

    // const getConnectionVal = (cn, mode, valueType = 'cost') => {
    //     let connection = getCityFromName(cn)?.[`connections_${mode}`].find((connection) => connection.to_id === from.id);
    //     return connection ? Math.round(connection[valueType]) : [];
    // };

    const updateCity = (index, newCity) => {
        setCities((prevCities) => {
            const updatedCities = [...prevCities];
            updatedCities[index] = newCity;
            return updatedCities;
        });
    };

    const updateData = () => {
        if (city1Name) {
            const cityData = getCityFromName(city1Name);
            console.log("city1Data", cityData);
            updateCity(0, cityData)
        }
        if (city2Name) {
            const cityData = getCityFromName(city2Name);
            updateCity(1, cityData)
        }
        if (city3Name) {
            const cityData = getCityFromName(city3Name);
            updateCity(2, cityData)
        }
    }

    // Base chart options for shared properties
    const baseOptions = {
        title: {
            align: 'left',
        },
        dataLabels: {
            enabled: false,
        },
    };

    // Helper function to generate series data for cities
    const generateSeries = (cityNames, dataFunc) => {
        return cityNames.map(cityName => ({
            name: cityName,
            data: typeof dataFunc === 'function' ? dataFunc(cityName) : dataFunc[cityName],
        }));
    };

    // Define city names and data
    const citiesGraph = [city1Name, city2Name, city3Name];

    // Chart-specific options
    const options1 = {
        ...baseOptions,
        chart: {
            type: 'bar',
            // stacked: true,
            // stackType: '100%'
        },
        title: {
            ...baseOptions.title,
            text: 'CO2 emissions per mode of transport',
        },
        xaxis: {
            categories: ['Flight', 'Car', 'Train'],
        },
        dataLabels: {
            enabled: true,
        },
        tooltip: {
            y: {
                formatter: function (val) {
                    return calculateOffset(
                        'emission',
                        averages,
                        val === null ? 0 : val
                    ) + " % CO2e"
                }
            }
        },
        annotations: {
            yaxis: [
                {
                    y: averages.co2, // Set threshold value
                    borderColor: '#FF4560',
                    label: {
                        borderColor: '#FF4560',
                        style: {
                            color: '#fff',
                            background: '#FF4560',
                        },
                        text: 'Average CO2e Threshold',
                    },
                },
            ],
        }
    };

    const options2 = {
        ...baseOptions,
        chart: {
            type: 'area',
        },
        title: {
            ...baseOptions.title,
            text: 'Seasonality',
        },
        xaxis: {
            categories: months,
        },
        stroke: {
            curve: 'smooth',
            width: 4,
        },
        annotations: {
            yaxis: [
                {
                    y: averages.seasonality / 1000, // Set threshold value
                    borderColor: '#FF4560',
                    label: {
                        borderColor: '#FF4560',
                        style: {
                            color: '#fff',
                            background: '#FF4560',
                        },
                        text: 'Seasonality Threshold',
                    },
                },
            ],
        }
    };

    const options3 = {
        ...baseOptions,
        chart: {
            type: 'bar',
        },
        title: {
            ...baseOptions.title,
            text: 'General stats',
        },
        xaxis: {
            categories: xLabels,
        },
    };

    const options4 = {
        ...baseOptions,
        chart: {
            type: 'bar',
        },
        title: {
            ...baseOptions.title,
            text: 'Costs per mode of transport',
        },
        xaxis: {
            categories: ['Flight', 'Car', 'Train'],
        }
    };

    const options5 = {
        ...baseOptions,
        chart: {
            type: 'line',
        },
        title: {
            ...baseOptions.title,
            text: 'Average Temperature',
        },
        xaxis: {
            categories: months,
        },
        stroke: {
            curve: 'straight',
            width: 4,
        },
        yaxis: {
            labels: {
                formatter: (val) => {
                    return val + '°C'
                }
            }
        }
    };

    // Series definitions using helper functions
    const series1 = generateSeries(citiesGraph, cityName => [
        getFlightVal(cityName, false) || 0,
        getDrivingVal(cityName, false) || 0,
        getTrainVal(cityName, false) || 0,
    ]);

    const series2 = generateSeries(
        citiesGraph,
        cityName => Object.values(getCitySeasonality(cityName))
    ).map(cityData => ({
        ...cityData,
        data: cityData.data.map(value => (value > 1 ? 0.05 : value))
    }));

    const series3 = generateSeries(
        citiesGraph,
        cityName =>  Object.values(getCityScores(cityName))
    ).map(cityData => ({
        ...cityData,
        data: cityData.data
    }))

    const series4 = generateSeries(citiesGraph, cityName => [
        getFlightVal(cityName, true) || 0,
        getDrivingVal(cityName, true) || 0,
        getTrainVal(cityName, true) || 0,
    ]);

    const series5 = generateSeries(
        citiesGraph,
        cityName => getCityTemperature(cityName)
            .map(monthData => ({
                x: monthData.month.slice(0, 3),
                y: [parseInt(monthData.avg)]
            }))
    );

    const getFilteredOptions = (excludeCities) => {
        excludeCities.push(fromDestination?.name);
        return destinations
            .filter(
                (destination) =>
                    !excludeCities.includes(destination.name)
            )
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    // Reusable ChartCard component
    const ChartCard = ({ options, series, type }) => (
        <Card style={{ maxWidth: '95%', margin: '35px auto' }}>
            <Chart options={options} series={series} type={type} height={300} />
        </Card>
    );

    const CityAutocomplete = ({
        cityName,
        setCityName,
        cityInput,
        setCityInput,
        filteredOptions,
        label = "To",
    }) => (
        <Autocomplete
            {...defaultProps}
            options={getFilteredOptions(filteredOptions)}
            id="from-destination"
            clearOnEscape
            value={cityName ? destinations.find((option) => option.name === cityName) : null}
            onChange={(event, newValue) => {
                if (newValue) {
                    setCityName(newValue.name);
                } else {
                    setCityName("");
                }
            }}
            inputValue={cityInput}
            onInputChange={(event, newInputValue) => {
                setCityInput(newInputValue);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required
                    variant="outlined"
                    fullWidth
                    sx={{ backgroundColor: Colors.white, borderRadius: "4px" }}
                />
            )}
        />
    );

    return (
        <>
            <Grid container spacing={2} sx={{ p: 3 }}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                    <Autocomplete
                        {...defaultProps}
                        options={getFilteredOptions([city2Name, city3Name])}
                        id="from-destination"
                        clearOnEscape
                        // value={city1Name ? { name: city1Name } : null}  // Preset value here
                        value={city1Name ? destinations.find((option) => option.name === city1Name) : null}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                setCity1Name(newValue.name);
                                // setCity1Id(getidFromCity(newValue.name));  // Ensure `newValue.name` here
                            } else {
                                setCity1Name("");
                                // setCity1Id(null);  // Clear the values when selection is cleared
                            }
                        }}
                        inputValue={city1}
                        onInputChange={(event, newInputValue) => {
                            setCity1(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="To"
                                required
                                variant="outlined"
                                fullWidth
                                sx={{ backgroundColor: Colors.white, borderRadius: "4px" }}
                            />
                        )}
                    />

                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                    <Autocomplete
                        {...defaultProps}
                        options={getFilteredOptions([city1Name, city3Name])}
                        id="from-destination"
                        clearOnEscape
                        // value={city1Name ? { name: city1Name } : null}  // Preset value here
                        value={city2Name ? destinations.find((option) => option.name === city2Name) : null}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                setCity2Name(newValue.name);
                                // setCity2Id(getidFromCity(newValue.name));  // Ensure `newValue.name` here
                            } else {
                                setCity2Name("");
                                // setCity2Id(null);  // Clear the values when selection is cleared
                            }
                        }}
                        inputValue={city2}
                        onInputChange={(event, newInputValue) => {
                            setCity2(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="To"
                                required
                                variant="outlined"
                                fullWidth
                                sx={{ backgroundColor: Colors.white, borderRadius: "4px" }}
                            />
                        )}
                    />

                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <Autocomplete
                        {...defaultProps}
                        options={getFilteredOptions([city2Name, city1Name])}
                        id="from-destination"
                        clearOnEscape
                        // value={city1Name ? { name: city1Name } : null}  // Preset value here
                        value={city3Name ? destinations.find((option) => option.name === city3Name) : null}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                setCity3Name(newValue.name);
                                // setCity3Id(getidFromCity(newValue.name));  // Ensure `newValue.name` here
                            } else {
                                setCity3Name("");
                                // setCity3Id(null);  // Clear the values when selection is cleared
                            }
                        }}
                        inputValue={city3}
                        onInputChange={(event, newInputValue) => {
                            setCity3(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="To"
                                required
                                variant="outlined"
                                fullWidth
                                sx={{ backgroundColor: Colors.white, borderRadius: "4px" }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ p: 3 }}>
                {
                    cities.map((toDest, index) => {
                        let adjustedIndex = 0 + index;
                        if (toDest) {
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={toDest.id}>
                                    {/* <CityCardItem
                                        index={adjustedIndex++}
                                        fromDestination={fromDestination}
                                        toDestination={toDest}
                                        month={month}
                                        minCo2Mode={calculateMinCo2Mode(fromDestination, toDest)}
                                        averages={averages}
                                        sortBy={'overall'}
                                        clicked={clickedItems[toDest.id]}
                                        onItemClicked={() => handleItemClick(toDest.id)}
                                        state={state}
                                    /> */}
                                    <CityCardTable toDestination={toDest} month={month} minCo2Mode={calculateMinCo2Mode(fromDestination, toDest)} averages={averages} userInterests={interests} cityInterests={toDest.interests} />
                                </Grid>
                            );
                        }
                    })
                }
            </Grid>
            <ChartCard options={options1} series={series1} type="bar" />
            <ChartCard options={options2} series={series2} type="area" />
            <ChartCard options={options3} series={series3} type="bar" />
            <ChartCard options={options4} series={series4} type="bar" />
            <ChartCard options={options5} series={series5} type="line" />
        </>
    );
};

export default Compare;
